import * as React from 'react';
import ReactSelect, { ValueComponentProps } from 'react-select';

import 'react-select/dist/react-select.css';
import './style.scss';

interface ISelectInputProps {
  name: string;
  value: any;
  clearable?: boolean;
  options: Array<{ value: any; label: string; disabled?: boolean }>;
  onChange: TButtonOnClick;
  onBlur?: TButtonOnClick;
  onFocus?: TButtonOnClick;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  multi?: boolean;
  styles?: any;
  className?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
}

type AccessibleValueProps = React.PropsWithChildren<ValueComponentProps<any>>;

const sanitizeInstanceId = (value: string) => value.trim().replace(/[^a-zA-Z0-9-_]+/g, '-');

let instanceCounter = 0;

const buildInstanceId = (props: ISelectInputProps) => {
  const rawId = props.name || props.ariaLabel || props.ariaLabelledBy;
  if (rawId) {
    return sanitizeInstanceId(rawId);
  }

  instanceCounter += 1;
  return `select-${instanceCounter}`;
};

class AccessibleSelectValue extends React.Component<AccessibleValueProps> {
  private dragging = false;

  handleMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    if (event.type === 'mousedown' && 'button' in event && event.button !== 0) {
      return;
    }
    if (this.props.onClick) {
      event.stopPropagation();
      this.props.onClick(this.props.value, event as React.MouseEvent<HTMLAnchorElement>);
      return;
    }
    if (this.props.value?.href) {
      event.stopPropagation();
    }
  };

  handleTouchEndLabel = (event: React.TouchEvent<HTMLElement>) => {
    this.handleMouseDown(event as unknown as React.MouseEvent<HTMLElement>);
  };

  onRemove = (event: React.MouseEvent<HTMLElement>) => {
    if (!this.props.onRemove) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.props.onRemove(this.props.value);
  };

  handleTouchEndRemove = (event: React.TouchEvent<HTMLElement>) => {
    if (this.dragging) {
      return;
    }
    this.onRemove(event as unknown as React.MouseEvent<HTMLElement>);
  };

  handleTouchMove = () => {
    this.dragging = true;
  };

  handleTouchStart = () => {
    this.dragging = false;
  };

  renderRemoveIcon() {
    if (this.props.disabled || !this.props.onRemove) {
      return null;
    }

    return (
      <span
        className="Select-value-icon"
        aria-hidden="true"
        onMouseDown={this.onRemove}
        onTouchEnd={this.handleTouchEndRemove}
        onTouchStart={this.handleTouchStart}
        onTouchMove={this.handleTouchMove}
      >
        ×
      </span>
    );
  }

  renderLabel() {
    const className = 'Select-value-label';
    const { value } = this.props;

    if (this.props.onClick || value?.href) {
      return (
        <a
          className={className}
          href={value?.href}
          target={value?.target}
          onMouseDown={this.handleMouseDown}
          onTouchEnd={this.handleTouchEndLabel}
        >
          {this.props.children}
        </a>
      );
    }

    return (
      <span className={className} id={this.props.id}>
        {this.props.children}
      </span>
    );
  }

  render() {
    const { value } = this.props;
    const className = ['Select-value', value?.disabled ? 'Select-value-disabled' : '', value?.className]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={className} style={value?.style} title={value?.title}>
        {this.renderRemoveIcon()}
        {this.renderLabel()}
      </div>
    );
  }
}

export default class SelectInput extends React.Component<ISelectInputProps, {}> {
  private readonly instanceId: string;

  public static defaultProps = {
    name: '',
    value: '',
    clearable: false,
    onChange: null,
    placeholder: 'Select Option',
    options: null,
    disabled: false,
    multi: false,
    className: '',
  };

  constructor(props: ISelectInputProps) {
    super(props);
    this.instanceId = buildInstanceId(props);
  }

  onChange = (e) => {
    if (this.props.multi) {
      const newValue = e.map((option) => option.value);
      this.props.onChange({
        target: { name: this.props.name, value: newValue },
        currentTarget: { name: this.props.name, value: newValue },
      });
    } else {
      this.props.onChange({
        target: { name: this.props.name, value: e.value },
        currentTarget: { name: this.props.name, value: e.value },
      });
    }
  };

  render() {
    const { name, options, disabled, value, clearable, placeholder, multi, ariaLabel, ariaLabelledBy } = this.props;
    const listboxId = `react-select-${this.instanceId}--list`;
    const inputProps = {
      ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      ...(ariaLabelledBy ? { 'aria-labelledby': ariaLabelledBy } : {}),
      'aria-controls': listboxId,
    };

    return (
      <ReactSelect
        value={value}
        name={name}
        options={options}
        clearable={clearable}
        onChange={(e) => this.onChange(e)}
        onBlur={this.props.onBlur}
        onFocus={this.props.onFocus}
        placeholder={placeholder}
        disabled={disabled}
        searchable={false}
        multi={multi}
        closeOnSelect={!multi}
        className={this.props.className}
        inputProps={inputProps}
        instanceId={this.instanceId}
        valueComponent={AccessibleSelectValue}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      />
    );
  }
}
